import type { HumanFace, HumanRigId } from './human.js';
import { humanoidMouths, humanoidEyes, humanoidEyebrows, humanoidNoses } from './humanoid-face-features.js';
import { humanMakeup, humanMakeupStyles, humanTattoos, humanTattooLayouts, humanTattooSizes, humanTattooColors } from './human-markings.js';

/**
 * Interchangeable head parts for the human rigs. Each slot has one variant per look; a rig asset carries every
 * variant as its own tagged meshes on the shared skeleton, and dressing a unit keeps only the variants it wears.
 * The first variant of a slot is its default, so a look that names no variant, or an older save, still dresses.
 */
export interface HumanPartVariant {
  id: string; name: string;
  /** Previously saved ids which now resolve to this variant. */
  legacyIds?: string[];
  /** Bodies this variant is offered on; absent means every body. */
  bodies?: HumanRigId[];
}
export interface HumanFaceVariant extends HumanPartVariant {
  /** Applied over the rig's own face: multipliers first, then additions. */
  scale?: Partial<HumanFace>; add?: Partial<HumanFace>;
}
export interface HumanPartSlot { name: string; variants: HumanPartVariant[]; defaultBy?: { slot: string; values: Record<string, string> } }

/**
 * Face shapes, each a bold departure from the rig's own face in width and length, jaw, cheekbones, nose, eyes and
 * mouth. The skull above the brow is shared by all of them, so every hairstyle fits every face.
 */
export const humanFaces: HumanFaceVariant[] = [
  { id: 'standard', name: 'Classic' },
  { id: 'angular', name: 'Angular', scale: { width: .96, length: 1.04, jaw: .94, cheek: 2.4, nose: .88, noseSize: 1.05, eyeSize: .92, brow: 1.3 }, add: { square: .22, chinDepth: .002, eyeTilt: .07, hollow: .0025 } },
  { id: 'broad', name: 'Broad', scale: { width: 1.06, length: .96, jaw: 1.04, chin: 1.1, brow: 2, nose: 1.22, lips: 1.12, eyeSize: .94 }, add: { square: .22, bridge: -.0012 } },
  { id: 'soft', name: 'Soft', scale: { width: 1.06, length: .94, jaw: 1, chin: .94, cheek: .3, nose: .96, noseSize: .92, eyeSize: 1.1, lips: 1.16 }, add: { fullness: .0015, chinDepth: -.002, square: -.4 } },
  { id: 'aquiline', name: 'Aquiline', scale: { width: .98, length: 1.02, noseSize: 1.1, nose: .94, eye: 1.5, brow: 1.5, lips: .9 }, add: { bridge: .0045, noseLength: .07, eyeTilt: -.04, chinDepth: .002 } },
  { id: 'gaunt', name: 'Gaunt', scale: { width: .93, length: 1.06, jaw: .96, cheek: 2.8, eye: 1.5, brow: 1.5, lips: .82, nose: .9, noseSize: 1.06 }, add: { hollow: .004, square: .1 } },
  { id: 'youthful', name: 'Youthful', scale: { width: 1.02, length: .91, jaw: .96, chin: .94, noseSize: .9, eyeSize: 1.15, brow: .6, lips: 1.08 }, add: { fullness: .001, square: -.3, chinDepth: -.002 } },
];

/**
 * Hairstyles. `standard` is each body's classic cut (a short crop or a chin-length bob); the other body's classic
 * is offered by name, so neither is listed twice.
 */
export const humanHairstyles: HumanPartVariant[] = [
  { id: 'standard', name: 'Classic' },
  { id: 'crop', name: 'Short crop', bodies: ['human-female'] },
  { id: 'bob', name: 'Bob', bodies: ['human-male'] },
  { id: 'buzz', name: 'Close-cropped' },
  { id: 'shaved', name: 'Shaved' },
  { id: 'swept', name: 'Swept back' },
  { id: 'tail', name: 'Tied tail' },
  { id: 'braid', name: 'Braid' },
  { id: 'topknot', name: 'Topknot' },
  { id: 'long', name: 'Long and loose' },
  { id: 'halfup', name: 'Half up' },
  { id: 'twin', name: 'Twin braids' },
  { id: 'locks', name: 'Dreadlocks' },
];

export const humanBeards: HumanPartVariant[] = [
  { id: 'none', name: 'None' },
  { id: 'stubble', name: 'Stubble' },
  { id: 'moustache', name: 'Moustache' },
  { id: 'goatee', name: 'Goatee' },
  { id: 'short', name: 'Short beard' },
  { id: 'full', name: 'Full beard' },
  { id: 'long', name: 'Long beard' },
  { id: 'braided', name: 'Braided beard' },
];

/** Old wounds on the face (drawn in `client/rendering/entities/face-marks.ts`): a healed cut's raised line, or a burn's mottled patch. */
export const humanScars: HumanPartVariant[] = [
  { id: 'none', name: 'None' },
  { id: 'cheek', name: 'Cheek slash' },
  { id: 'eye', name: 'Scarred eye' },
  { id: 'brow', name: 'Split brow' },
  { id: 'lip', name: 'Split lip' },
  { id: 'jaw', name: 'Jaw scar' },
  { id: 'chin', name: 'Chin scar' },
  { id: 'forehead', name: 'Forehead gash' },
  { id: 'nose', name: 'Broken nose' },
  { id: 'veteran', name: 'Old wounds' },
];
export const humanBurns: HumanPartVariant[] = [
  { id: 'none', name: 'None' },
  { id: 'cheek', name: 'Burned cheek' },
  { id: 'half', name: 'Half-face burn' },
  { id: 'jaw', name: 'Burned jaw' },
  { id: 'brow', name: 'Burned brow' },
];

/**
 * Headwear is worn on its own, over any armor: hoods, bandanas and caps of cloth and leather, and helms of steel. `HEADWEAR_COVERS` lists the head
 * parts a piece hides outright (a great helm hides the face's beard). Hair is not listed: it is pressed in under the piece and only what the piece
 * leaves out is drawn (see `hair-fit.ts`). Headwear is cosmetic.
 */
export const humanHeadwear: HumanPartVariant[] = [
  { id: 'none', name: 'None' },
  { id: 'hood', name: 'Hood' },
  { id: 'masked-hood', name: 'Masked hood' },
  { id: 'bandana', name: 'Bandana' },
  { id: 'feathered-cap', name: 'Feathered cap' },
  { id: 'leather-cap', name: 'Leather cap' },
  { id: 'skullcap', name: 'Steel skullcap' },
  { id: 'kettle-hat', name: 'Kettle hat' },
  { id: 'bascinet', name: 'Bascinet' },
  { id: 'horned-helm', name: 'Horned helm' },
  { id: 'winged-helm', name: 'Winged helm' },
  { id: 'great-helm', name: 'Great helm' },
  { id: 'circlet', name: 'Circlet' },
];
export const HEADWEAR_COVERS: Record<string, string[]> = { 'great-helm': ['beard'] };
/** Cloaks hang from the shoulders and are chosen apart from the armor: none, a short cape, a long cloak or a ragged one. */
export const humanCapes: HumanPartVariant[] = [
  { id: 'none', name: 'None' },
  { id: 'short', name: 'Short cape' },
  { id: 'long', name: 'Long cloak' },
  { id: 'ragged', name: 'Ragged cloak' },
];

/**
 * How a figure stands at ease: a layer over the relaxed idle in each body's animator (`BEARINGS`). Presentation only; combat, walking and
 * gestures replace it as they replace the idle.
 */
export const humanBearings: HumanPartVariant[] = [
  { id: 'neutral', name: 'Neutral' },
  { id: 'aggressive', name: 'Aggressive' },
  { id: 'stooped', name: 'Old and stooped' },
  { id: 'apprehensive', name: 'Apprehensive' },
  { id: 'authoritative', name: 'Authoritative' },
  { id: 'weary', name: 'Weary' },
];
export type HumanBearing = 'neutral' | 'aggressive' | 'stooped' | 'apprehensive' | 'authoritative' | 'weary';

export const humanPartSlots: Record<string, HumanPartSlot> = {
  face: { name: 'Face', variants: humanFaces },
  mouth: { name: 'Mouth', variants: humanoidMouths },
  eyes: { name: 'Eyes', variants: humanoidEyes },
  eyebrows: { name: 'Eyebrows', variants: humanoidEyebrows },
  nose: { name: 'Nose', variants: humanoidNoses },
  scar: { name: 'Scar', variants: humanScars },
  burn: { name: 'Burn', variants: humanBurns },
  hair: { name: 'Hairstyle', variants: humanHairstyles },
  beard: { name: 'Facial hair', variants: humanBeards },
  makeup: { name: 'Makeup colours', variants: humanMakeup },
  makeupStyle: { name: 'Makeup style', variants: humanMakeupStyles,
    defaultBy: { slot: 'makeup', values: Object.fromEntries(humanMakeup.filter(preset => preset.id !== 'none').map(preset => [preset.id, preset.id === 'warpaint' ? 'warpaint' : 'winged'])) } },
  tattoo: { name: 'Tattoo design', variants: humanTattoos },
  tattooLayout: { name: 'Tattoo placement', variants: humanTattooLayouts },
  tattooSize: { name: 'Tattoo size', variants: humanTattooSizes },
  tattooColor: { name: 'Tattoo ink', variants: humanTattooColors },
  headwear: { name: 'Headwear', variants: humanHeadwear },
  cape: { name: 'Cloak', variants: humanCapes },
  bearing: { name: 'Bearing', variants: humanBearings },
};

/** What each rig draws for `standard` hair. */
const classicHair: Record<HumanRigId, string> = { 'human-male': 'crop', 'human-female': 'bob' };

const signedFace = new Set<keyof HumanFace>(['bridge', 'chinDepth', 'eyeTilt', 'noseLength']);
const offered =(variant: HumanPartVariant, rig: string) => !variant.bodies || variant.bodies.includes(rig as HumanRigId);
/** The variants of a slot that this rig offers, default first. */
export function humanPartVariants(slot: string, rig: string): HumanPartVariant[] {
  return humanPartSlots[slot]?.variants.filter(variant => offered(variant, rig)) ?? [];
}
/** Every slot resolved to a variant this rig offers; unknown or missing choices fall back to the slot's default. */
export function humanParts(rig: string, chosen: Record<string, string> = {}): Record<string, string> {
  return Object.fromEntries(Object.keys(humanPartSlots).map(slot => {
    const variants = humanPartVariants(slot, rig);
    const dependency = humanPartSlots[slot].defaultBy, inherited = dependency?.values[chosen[dependency.slot]];
    const selected = variants.find(variant => variant.id === chosen[slot] || variant.legacyIds?.includes(chosen[slot]));
    return [slot, selected?.id ?? inherited ?? variants[0].id];
  }));
}
/** The geometry a hair variant draws on this rig. */
export function humanHairStyle(rig: string, id: string): string {
  return id === 'standard' ? classicHair[rig as HumanRigId] ?? 'crop' : id;
}
/** The rig's face with a face variant applied. */
export function humanFace(base: HumanFace, id: string): HumanFace {
  return applyFaceVariant(base, humanFaces.find(face => face.id === id));
}
/** A face with one variant's multipliers and additions applied over `base`. */
export function applyFaceVariant(base: HumanFace, variant?: HumanFaceVariant): HumanFace {
  const face = { ...base };
  for (const [key, value] of Object.entries(variant?.scale ?? {})) face[key as keyof HumanFace] *= value;
  for (const [key, value] of Object.entries(variant?.add ?? {})) {
    const field = key as keyof HumanFace, sum = face[field] + value;
    // Sculpt amounts never invert; profile offsets (a hooked or flat bridge, a receding chin, tilted eyes) may.
    face[field] = signedFace.has(field) ? sum : Math.max(0, sum);
  }
  return face;
}
